import { useMemo, useState } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import { useQuery, useMutation } from "@tanstack/react-query"
import { Layout } from "../components/Layout"
import { PageHeader } from "@/components/PageHeader"
import { Card } from "@/components/Card"
import { Button } from "@/components/Button"
import { Input } from "@/components/Form/Input"
import { Dialog } from "@/components/Dialog/Dialog"
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog/Dialog"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { Skeleton } from "@/components/Skeleton/Skeleton"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { DataTable } from "@/components/DataTable/DataTable"
import { useToast } from "@/components/Toast"
import { client, APIError } from "@/api"
import { useAttachTeamToOrganization } from "@/hooks/useOrganizations"
import { useCreateTeam } from "@/hooks/useTeams"
import { useSubscriptionPlans } from "@/hooks/useSubscriptionPlans"
import { createTeamToast } from "@/lib/createTeamResult"
import {
  buildTeamAccessPayload,
  defaultTeamAccessChoice,
  teamAccessChoiceError,
  teamAccessConfirmCopy,
  teamAccessSubmitLabel,
  type TeamAccessChoice,
} from "@/lib/createTeamAccess"
import { TeamAccessChooser } from "@/components/TeamAccessChooser"
import { ColumnDef } from "@tanstack/react-table"
import {
  ArrowLeftIcon,
  BuildingOffice2Icon,
  UsersIcon,
  CalendarIcon,
  LinkIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"

type OrgTeamItem = {
  public_id: string
  name: string
  created_at?: string
}

type OrgDetailResponse = {
  ok: true
  organization: {
    public_id: string
    name: string
    created_at?: string
  }
  // Optional fields depending on backend scope; tolerate absence
  stats?: {
    teamCount?: number
  }
  teams?: OrgTeamItem[]
  subscriptions?: {
    stripe_subscription_id: string | null
    plan_id: string
    status: string
    current_period_end: string | null
  }[]
  admins?: { public_id: string; name: string | null }[]
}

export default function OrganizationDetail() {
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const { data, isLoading, error, refetch } = useQuery<OrgDetailResponse>({
    queryKey: ["admin-organization", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const res = await (client.admin.organizations[":orgPublicId"] as any)["$get"]({
        param: { orgPublicId: orgId! },
      })
      if (!res.ok) {
        const err = (await res
          .clone()
          .json()
          .catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "ORG_DETAIL_FAILED",
          new Error(err.message || "Failed to load organization")
        )
      }
      return res.json() as Promise<OrgDetailResponse>
    },
  })

  const [billingForm, setBillingForm] = useState({
    stripeSubscriptionId: "",
    expectedTeamPublicId: "",
    reason: "",
  })
  const [billingPreview, setBillingPreview] = useState<{
    stripe_subscription_id: string
    plan: string
    current_period_end: string | null
    teams: { public_id: string; name: string }[]
  } | null>(null)
  const [showBillingConfirm, setShowBillingConfirm] = useState(false)
  const [verifiedAccess, setVerifiedAccess] = useState<
    {
      team_public_id: string
      game_upload_allowed: boolean
      storage_limit_gb: number
      storage_used_bytes: number
    }[]
  >([])
  const linkSubscription = useMutation({
    mutationFn: async (apply: boolean) => {
      const response = await client.admin.organizations[":orgPublicId"]["link-subscription"].$post({
        param: { orgPublicId: orgId! },
        json: { ...billingForm, apply },
      })
      if (!response.ok) {
        const body = (await response.json()) as { message?: string }
        throw new Error(body.message || "Unable to link subscription")
      }
      return response.json()
    },
    onSuccess: (result) => {
      if (result.applied) {
        setVerifiedAccess(result.access)
        setBillingPreview(null)
        setBillingForm({ stripeSubscriptionId: "", expectedTeamPublicId: "", reason: "" })
        toast({
          title: "Paid subscription linked",
          description: "The organization’s teams now inherit this subscription.",
        })
        void refetch()
      } else setBillingPreview(result.preview)
    },
  })

  // Attach existing team dialog
  const [showAttachTeamDialog, setShowAttachTeamDialog] = useState(false)
  const [attachForm, setAttachForm] = useState({ team_public_id: "" })
  const attachTeam = useAttachTeamToOrganization()

  // Create team dialog
  const [showCreateTeamDialog, setShowCreateTeamDialog] = useState(false)
  const [createTeamForm, setCreateTeamForm] = useState({
    name: "",
    league: "",
    level: "",
    creatorEmail: "",
  })
  const [createTeamAccess, setCreateTeamAccess] = useState<TeamAccessChoice>(() =>
    defaultTeamAccessChoice()
  )
  const [showCreateTeamAccessConfirm, setShowCreateTeamAccessConfirm] = useState(false)
  const createTeamForOrg = useCreateTeam()
  const { data: plansData } = useSubscriptionPlans()
  const createTeamPlanName = plansData?.plans?.find(
    (plan) => plan.id === createTeamAccess.planId
  )?.name

  // Delete org (not implemented backend in minimal scope) — placeholder wiring
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const teams: OrgTeamItem[] = data?.teams ?? []

  const teamColumns = useMemo<ColumnDef<OrgTeamItem>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Team",
        cell: ({ row }) => (
          <div className="flex items-center">
            <UsersIcon className="h-4 w-4 text-secondary mr-2" />
            <span className="font-medium">{row.original.name}</span>
          </div>
        ),
      },
      {
        accessorKey: "public_id",
        header: "Public ID",
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.public_id}</span>,
      },
      {
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <div className="flex items-center">
            <CalendarIcon className="h-4 w-4 text-secondary mr-1" />
            <span className="text-sm">
              {row.original.created_at
                ? new Date(row.original.created_at).toLocaleDateString()
                : "—"}
            </span>
          </div>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-2">
            <Link to={`/teams/${row.original.public_id}`} className="text-sm underline">
              View
            </Link>
          </div>
        ),
      },
    ],
    []
  )

  const onAttachTeamSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgId || !attachForm.team_public_id.trim()) return
    try {
      await attachTeam.mutateAsync({
        orgPublicId: orgId,
        teamPublicId: attachForm.team_public_id.trim(),
      })
      toast({
        title: "Team attached",
        description: `Attached team ${attachForm.team_public_id.trim()} to ${orgId}`,
      })
      setAttachForm({ team_public_id: "" })
      setShowAttachTeamDialog(false)
      refetch()
    } catch (e) {
      toast({
        title: "Error attaching team",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  const hasOrganizationBilling =
    data?.subscriptions?.some((sub) => !["cancelled", "canceled"].includes(sub.status)) ?? false

  // Validate, then confirm anything that provisions in Stripe (comp or trial)
  // before creating. A plain "no plan yet" create needs no confirmation.
  const onCreateTeamSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgId || !createTeamForm.name.trim() || !createTeamForm.creatorEmail.trim()) return
    if (hasOrganizationBilling) {
      void runCreateTeam()
      return
    }
    const accessError = teamAccessChoiceError(createTeamAccess)
    if (accessError) {
      toast({ title: "Check the access section", description: accessError, variant: "destructive" })
      return
    }
    if (teamAccessConfirmCopy(createTeamAccess, createTeamForm.name.trim(), createTeamPlanName)) {
      setShowCreateTeamAccessConfirm(true)
      return
    }
    void runCreateTeam()
  }

  const runCreateTeam = async () => {
    if (!orgId) return
    try {
      const result = await createTeamForOrg.mutateAsync({
        organizationPublicId: orgId,
        name: createTeamForm.name.trim(),
        league: createTeamForm.league || undefined,
        level: createTeamForm.level || undefined,
        type: "football",
        creatorEmail: createTeamForm.creatorEmail.trim(),
        ...(hasOrganizationBilling ? {} : buildTeamAccessPayload(createTeamAccess)),
      })
      toast(createTeamToast(result, `Created team ${createTeamForm.name.trim()} under ${orgId}`))
      setCreateTeamForm({
        name: "",
        league: "",
        level: "",
        creatorEmail: "",
      })
      setCreateTeamAccess(defaultTeamAccessChoice())
      setShowCreateTeamDialog(false)
      refetch()
    } catch (e) {
      toast({
        title: "Error creating team",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-48" />
              <Skeleton className="h-64" />
            </div>
            <div className="space-y-6">
              <Skeleton className="h-48" />
              <Skeleton className="h-48" />
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  const { organization } = data

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          title={
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate("/organizations")}
                className="p-2 hover:bg-surface rounded-md transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <span>Organization</span>
            </div>
          }
          actions={
            <div className="flex items-center space-x-3">
              <Button variant="secondary" onClick={() => setShowAttachTeamDialog(true)}>
                <LinkIcon className="h-4 w-4" />
                Attach Team
              </Button>
              <Button onClick={() => setShowCreateTeamDialog(true)}>
                <PlusIcon className="h-4 w-4" />
                Create Team
              </Button>
              <Button variant="destructive" onClick={() => setShowDeleteDialog(true)} disabled>
                <TrashIcon className="h-4 w-4" />
                Delete
              </Button>
            </div>
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Org Info */}
            <div className="bg-surface rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <BuildingOffice2Icon className="h-8 w-8 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-medium">{organization.name}</h3>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-sm text-secondary">Organization ID</p>
                      <p className="font-mono text-sm">{organization.public_id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-secondary">Created</p>
                      <p className="text-sm">
                        {organization.created_at
                          ? new Date(organization.created_at).toLocaleDateString()
                          : "—"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-lg p-6 space-y-4">
              <h2 className="text-lg font-semibold">Organization billing</h2>
              <p className="text-sm text-secondary">
                Game and storage allowances are shared across the organization’s teams.
              </p>
              {data?.subscriptions?.map((subscription) => (
                <p key={subscription.stripe_subscription_id ?? subscription.plan_id}>
                  <span>
                    {subscription.plan_id} — {subscription.status}
                  </span>{" "}
                  ·{" "}
                  {subscription.current_period_end
                    ? `Current period ends ${new Date(subscription.current_period_end).toLocaleDateString()}`
                    : "No period end"}
                </p>
              ))}
              <p className="text-sm">
                Administrators:{" "}
                {data?.admins?.map((admin) => admin.name || admin.public_id).join(", ") ||
                  "None assigned"}
              </p>
              {verifiedAccess.map((access) => (
                <p key={access.team_public_id}>
                  {access.team_public_id}: game upload{" "}
                  {access.game_upload_allowed ? "allowed" : "at limit"}; shared storage{" "}
                  {(access.storage_used_bytes / 1024 ** 3).toFixed(1)} / {access.storage_limit_gb}{" "}
                  GB.
                </p>
              ))}
              <h3 className="font-medium">Link an existing paid subscription</h3>
              <p className="text-sm text-secondary">
                Move an existing team purchase to this organization. Its Stripe customer, price, and
                renewal settings stay the same.
              </p>
              <Input
                aria-label="Stripe subscription ID"
                placeholder="Stripe subscription ID (sub_…)"
                value={billingForm.stripeSubscriptionId}
                disabled={linkSubscription.isPending || !!billingPreview}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, stripeSubscriptionId: e.target.value })
                }
              />
              <Input
                aria-label="Current team public ID"
                placeholder="Current team public ID (t.… )"
                value={billingForm.expectedTeamPublicId}
                disabled={linkSubscription.isPending || !!billingPreview}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, expectedTeamPublicId: e.target.value })
                }
              />
              <Input
                aria-label="Reason for linking subscription"
                placeholder="Reason"
                value={billingForm.reason}
                disabled={linkSubscription.isPending || !!billingPreview}
                onChange={(e) => setBillingForm({ ...billingForm, reason: e.target.value })}
              />
              {linkSubscription.error && (
                <p role="alert" className="text-destructive">
                  {linkSubscription.error.message}
                </p>
              )}
              {billingPreview ? (
                <div className="space-y-3">
                  <p>
                    Link {billingPreview.plan} ({billingPreview.stripe_subscription_id}) to{" "}
                    {organization.name}.
                  </p>
                  <p>Covered teams: {billingPreview.teams.map((team) => team.name).join(", ")}.</p>
                  <Button
                    disabled={linkSubscription.isPending}
                    onClick={() => setShowBillingConfirm(true)}
                  >
                    Confirm subscription link
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={linkSubscription.isPending}
                    onClick={() => setBillingPreview(null)}
                  >
                    Edit
                  </Button>
                </div>
              ) : (
                <Button
                  disabled={
                    linkSubscription.isPending ||
                    !billingForm.stripeSubscriptionId ||
                    !billingForm.expectedTeamPublicId ||
                    !billingForm.reason
                  }
                  onClick={() => linkSubscription.mutate(false)}
                >
                  Preview subscription link
                </Button>
              )}
            </div>

            {/* Teams */}
            <div className="bg-surface rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Teams ({teams.length})</h2>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => setShowAttachTeamDialog(true)}>
                    <LinkIcon className="h-4 w-4" />
                    Attach Team
                  </Button>
                  <Button onClick={() => setShowCreateTeamDialog(true)}>
                    <PlusIcon className="h-4 w-4" />
                    Create Team
                  </Button>
                </div>
              </div>
              <DataTable columns={teamColumns} data={teams} searchKey="name" />
            </div>
          </div>

          {/* Quick Stats */}
          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Organization Stats</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <UsersIcon className="h-5 w-5 text-secondary" />
                    <span className="text-sm">Teams</span>
                  </div>
                  <span className="font-medium">{data.stats?.teamCount ?? teams.length}</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Attach Team Dialog */}
      <Dialog open={showAttachTeamDialog} onOpenChange={setShowAttachTeamDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Attach Existing Team</DialogTitle>
            <DialogDescription>
              Attach an existing team to this organization by public ID.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onAttachTeamSubmit} className="space-y-4">
            <Input
              label="Team Public ID"
              placeholder="t.abc123"
              value={attachForm.team_public_id}
              onChange={(e) => setAttachForm({ team_public_id: e.target.value })}
              required
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowAttachTeamDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={attachTeam.isPending || !attachForm.team_public_id.trim()}
              >
                {attachTeam.isPending ? "Attaching..." : "Attach Team"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Team Dialog */}
      <Dialog open={showCreateTeamDialog} onOpenChange={setShowCreateTeamDialog}>
        {/* The access chooser makes this form tall; keep the buttons reachable
            on a short laptop screen by capping the height and scrolling the body. */}
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Team for Organization</DialogTitle>
            <DialogDescription>
              Creates a team under this organization, owned by the coach below. Teams inherit an
              existing organization subscription.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onCreateTeamSubmit} className="space-y-4">
            <Input
              label="Team Name"
              placeholder="Springfield Tigers"
              value={createTeamForm.name}
              onChange={(e) => setCreateTeamForm((s) => ({ ...s, name: e.target.value }))}
              required
            />
            <Input
              label="Creator Email"
              type="email"
              placeholder="coach@example.com"
              value={createTeamForm.creatorEmail}
              onChange={(e) => setCreateTeamForm((s) => ({ ...s, creatorEmail: e.target.value }))}
              required
            />
            <p className="text-xs text-secondary -mt-2">
              The user who will own the team. They become an admin member.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="League"
                placeholder="unknown"
                value={createTeamForm.league}
                onChange={(e) => setCreateTeamForm((s) => ({ ...s, league: e.target.value }))}
              />
              <Input
                label="Level"
                placeholder="10U"
                value={createTeamForm.level}
                onChange={(e) => setCreateTeamForm((s) => ({ ...s, level: e.target.value }))}
              />
            </div>
            {hasOrganizationBilling ? (
              <p>This team will be covered by the organization’s subscription.</p>
            ) : (
              <TeamAccessChooser
                value={createTeamAccess}
                onChange={setCreateTeamAccess}
                plans={plansData?.plans ?? []}
                disabled={createTeamForOrg.isPending}
              />
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowCreateTeamDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  createTeamForOrg.isPending ||
                  !createTeamForm.name.trim() ||
                  !createTeamForm.creatorEmail.trim()
                }
              >
                {createTeamForOrg.isPending
                  ? "Creating..."
                  : hasOrganizationBilling
                    ? "Create Team"
                    : teamAccessSubmitLabel(createTeamAccess, createTeamPlanName)}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {(() => {
        const copy = teamAccessConfirmCopy(
          createTeamAccess,
          createTeamForm.name.trim(),
          createTeamPlanName
        )
        return (
          <ConfirmDialog
            open={showCreateTeamAccessConfirm}
            onOpenChange={setShowCreateTeamAccessConfirm}
            title={copy?.title ?? "Create team?"}
            description={copy?.description ?? ""}
            confirmLabel={copy?.confirmLabel ?? "Create team"}
            onConfirm={() => {
              setShowCreateTeamAccessConfirm(false)
              void runCreateTeam()
            }}
          />
        )
      })()}

      <ConfirmDialog
        open={showBillingConfirm}
        onOpenChange={setShowBillingConfirm}
        title="Link existing paid subscription?"
        description={`Associate ${billingPreview?.stripe_subscription_id ?? "this subscription"} with ${data?.organization.name ?? "this organization"} and its ${billingPreview?.teams.length ?? 0} teams. This updates ownership and access using the existing purchase.`}
        confirmLabel="Link subscription"
        onConfirm={() => { setShowBillingConfirm(false); linkSubscription.mutate(true) }}
      />

      {/* Delete Placeholder */}
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete Organization"
        description="Delete organization is not available in this scope."
        onConfirm={() => setShowDeleteDialog(false)}
      />
    </Layout>
  )
}
